import FullPageLoader from 'components/common/FullPageLoader';
import { Select } from 'components/common/Select';
import { TimeInput } from 'components/template/TemplateForm';
import React from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import Api from 'services/api';
import { useTranslation } from 'react-i18next';
import {
    buildInvoiceGroups,
    buildSpecialProjectGroups,
    getInvoiceGroupByConfig,
    getSpecialProjectGroupByConfig,
} from 'helpers/invoiceGrouping';

const show = (value) => value !== null && value !== undefined && value !== '' && value !== 'N/A';

const EditPdfForm = ({
    data,
    invoiceNumber,
    loader,
    formState,
    updateAppointmentField,
    updateSpecialProjectField,
    languages = [],
    facilities = [],
    totalBill = 0,
    appointmentTypeOptions = [],
    modalityOptions = [],
    commentOptions = [],
    control,
    clientId,
}) => {
    const { t } = useTranslation();
    const clientData = data?.client || data;

    const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
        try {
            const response = await Api.fetchLanguages(page, {
                pageLength: 20,
                sortBy: 'id',
                sortDirection: 'asc',
                search: inputValue,
            });
            const result = response?.data?.data;
            const data = Array.isArray(result?.data) ? result.data : [];
            const lastPage = result?.last_page;

            return {
                options: data.map((language) => ({ label: language.name, value: language.id })),
                hasMore: page !== lastPage,
                additional: { page: page + 1 },
            };
        } catch (error) {
            console.error(error);
            return { options: [], hasMore: false, additional: { page } };
        }
    };

    const filteredFacilitiesLoadOptions = async (inputValue, loadedOptions, { page }) => {
        try {
            let response = null;

            if (clientId) {
                response = await Api.fetchFacilitiesByTypeId(clientId);
            }

            if (!response || response?.status >= 400) {
                response = await Api.fetchFacilities(page, {
                    pageLength: 20,
                    sortBy: 'id',
                    sortDirection: 'asc',
                    search: inputValue,
                });
            }

            const list = Array.isArray(response?.data?.data)
                ? response.data.data
                : Array.isArray(response?.data)
                    ? response.data
                    : [];

            const filtered = inputValue
                ? list.filter((facility) =>
                    (facility.facility_name || '').toLowerCase().includes(inputValue.toLowerCase())
                )
                : list;

            return {
                options: filtered.map((facility) => ({ label: facility.facility_name, value: facility.id })),
                hasMore: false,
                additional: { page: page + 1 },
            };
        } catch (error) {
            console.error(error);
            return { options: [], hasMore: false, additional: { page } };
        }
    };

    const currentPhaseOptions = [
        { label: t('invoiceEditForm.translation'), value: 'translation' },
        { label: t('invoiceEditForm.editing'), value: 'editing' },
        { label: t('invoiceEditForm.review'), value: 'review' },
        { label: t('invoiceEditForm.audioRecording'), value: 'audio_recording' },
        { label: t('invoiceEditForm.other'), value: 'other' },
    ];
    const subAccountOptions = (clientData?.sub_accounts || []).map((account) => ({
        label: account.subaccount_name || account.name,
        value: account.id,
    }));
    const departmentOptions = (clientData?.departments || []).map((department) => ({
        label: department.department_name,
        value: department.id,
    }));
    const appointments = formState?.appointments || [];
    const groupByValue = clientData?.invoice_group_by || '';
    const groupByConfig = getInvoiceGroupByConfig(groupByValue);
    const groupedAppointments = buildInvoiceGroups(appointments, groupByConfig.join(','));
    console.log('groupedAppointments', groupedAppointments);
    const specialProjects = formState?.special_projects || [];
    // const specialProjectGroupByValue = clientData?.special_project_group_by
    //     || specialProjects[0]?.project?.translation_details?.invoice_group_by
    //     || '';
    const specialProjectGroupByValue = clientData?.invoice_group_by || '';
    const specialProjectGroupByConfig = getSpecialProjectGroupByConfig(specialProjectGroupByValue);
    const groupedSpecialProjects = buildSpecialProjectGroups(
        specialProjects,
        specialProjectGroupByConfig.join(',')
    );

    return (
        <>
            <div className="page">
                <header>
                    <div className="us">
                        <img src="/main-logo.png" alt="Logo" />
                    </div>
                    <div className="d-flex align-items-center">
                        {show(data?.invoice_number || invoiceNumber) && (
                            <div className="title">
                                <h4>{t('invoice.invoiceNumberTitle', { invoiceNumber: data?.invoice_number || invoiceNumber })}</h4>
                            </div>
                        )}
                        <div className="title">
                            <h4>
                                {t('invoice.dateLabel')}: {data?.billing_date
                                    ? new Date(data.billing_date).toLocaleDateString('en-GB')
                                    : new Date().toLocaleDateString('en-GB')}
                            </h4>
                        </div>
                    </div>
                </header>

                <section>
                    <div className="billed-to">
                        <h1>{t('invoice.billTo')}</h1>
                        {show(clientData?.user?.name) && <h2>{clientData.user.name}</h2>}
                        {show(clientData?.billing_person) && <h3>{clientData.billing_person}</h3>}
                        {show(clientData?.billing_email) && <h3>{clientData.billing_email}</h3>}
                        {show(clientData?.billing_zipcode || clientData?.city?.name) && (
                            <h3>{clientData?.billing_zipcode} {clientData?.city?.name}</h3>
                        )}
                        {show(clientData?.state?.name) && <h3>{clientData.state.name}</h3>}
                    </div>

                    <div className="invoice-table-wrapper">
                        {appointments.length > 0 && (
                        <h2 className="mb-3" style={{ fontWeight: 700 }}>{t('invoice.interpretation')}</h2>
                        )}
                        {appointments.length > 0 && (
                        <table className="invoice-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('invoice.description')}</th>
                                    <th>{t('invoice.totalCost')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedAppointments.map((group, groupIndex) => (
                                    <React.Fragment key={`${group.key}-${groupIndex}`}>
                                        {groupByConfig.length > 0 && (
                                            <tr>
                                                <td colSpan="3"><strong>{group.label}</strong></td>
                                            </tr>
                                        )}
                                        {group.appointments.map((appointment, index) => (
                                            <tr key={appointment.id || index}>
                                        <td>{index + 1}</td>
                                        <td>
                                            {show(appointment.appid) && (
                                                <div className="mb-2 font-bold" style={{ fontSize: '15px' }}>
                                                    <span>{t('invoice.appointmentNumber')}: {appointment.appid}</span>
                                                </div>
                                            )}

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.language')}</span>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    <AsyncPaginate
                                                        menuPlacement="auto"
                                                        styles={{ ...selectCustomStyles }}
                                                        value={
                                                            appointment.language_id
                                                                ? {
                                                                    value: appointment.language_id,
                                                                    label:
                                                                        appointment.languageName ||
                                                                        languages.find((language) => language.id === appointment.language_id)?.name ||
                                                                        '',
                                                                }
                                                                : null
                                                        }
                                                        loadOptions={filteredLanguagesLoadOptions}
                                                        isClearable={false}
                                                        escapeClearsValue
                                                        onChange={(obj) =>
                                                            updateAppointmentField(index, 'language_id', obj?.value, { languageName: obj?.label || '' })
                                                        }
                                                        additional={{ page: 1 }}
                                                    />
                                                </div>
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.date')}</span>
                                                </div>
                                                <div className="input-image" style={{ padding: '0px' }}>
                                                <input
                                                    className="input-in type_inp"
                                                    type="date"
                                                    value={appointment.scheduledDate || ''}
                                                    onChange={(event) => updateAppointmentField(index, 'scheduledDate', event.target.value)}
                                                />
                                                <img
                                                src="/images/celender.png"
                                                alt="celender Icon"
                                                className="custom-icon"
                                                />
                                                </div>
                                            </label>

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.modalityType')}</span>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    <Select
                                                        name={`type_${index}`}
                                                        options={appointmentTypeOptions}
                                                        value={appointmentTypeOptions.find((option) => option.value === appointment.type) || null}
                                                        isClearable={false}
                                                        disabledSearch={true}
                                                        onChange={(value) => updateAppointmentField(index, 'type', value)}
                                                    />
                                                </div>
                                            </label>

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.subjectType')}</span>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    <Select
                                                        name={`subject_${index}`}
                                                        options={modalityOptions}
                                                        value={modalityOptions.find((option) => option.value === appointment.subject) || null}
                                                        isClearable={false}
                                                        disabledSearch={true}
                                                        onChange={(value) => updateAppointmentField(index, 'subject', value)}
                                                    />
                                                </div>
                                            </label>

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.scheduledStartTime')}</span>
                                                </div>
                                                <TimeInput
                                                    name={`appointment_${index}_scheduledStartTime`}
                                                    control={control}
                                                    rule={false}
                                                    onChange={(value) => updateAppointmentField(index, 'scheduledStartTime', value)}
                                                />
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.scheduledDuration')}</span>
                                                </div>
                                                <input
                                                    className="input-in type_inp"
                                                    type="number"
                                                    min="0"
                                                    value={appointment.scheduledDuration || ''}
                                                    onChange={(event) => updateAppointmentField(index, 'scheduledDuration', event.target.value)}
                                                />
                                            </label>

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.durationUnit')}</span>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    <Select
                                                        name={`duration_unit_${index}`}
                                                        options={[{ label: 'Minute', value: 'minute' }, { label: 'Hour', value: 'hour' }]}
                                                        value={[{ label: 'Minute', value: 'minute' }, { label: 'Hour', value: 'hour' }].find((option) => option.value === (appointment.scheduledDurationUnit || 'minute')) || null}
                                                        isClearable={false}
                                                        disabledSearch={true}
                                                        onChange={(value) => updateAppointmentField(index, 'scheduledDurationUnit', value)}
                                                    />
                                                </div>
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.scheduledEndTime')}</span>
                                                </div>
                                                <input className="input-in type_inp" type="time" value={appointment.scheduledEndTime || ''} readOnly />
                                            </label>

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.actualStartTime')}</span>
                                                </div>
                                                <TimeInput
                                                    name={`appointment_${index}_actualStartTime`}
                                                    control={control}
                                                    rule={false}
                                                    onChange={(value) => updateAppointmentField(index, 'actualStartTime', value)}
                                                />
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.actualDurationMins')}</span>
                                                </div>
                                                <input
                                                    className="input-in type_inp"
                                                    type="number"
                                                    min="0"
                                                    value={appointment.actualDurationMinutes || ''}
                                                    onChange={(event) => updateAppointmentField(index, 'actualDurationMinutes', event.target.value)}
                                                />
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.actualEndTime')}</span>
                                                </div>
                                                <input className="input-in type_inp" type="time" value={appointment.actualEndTime || ''} readOnly />
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.totalBillableDurationMins')}</span>
                                                </div>
                                                <input className="input-in type_inp" type="number" value={appointment.totalBillableDuration || ''} readOnly />
                                            </label>

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.facilityName')}</span>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    <AsyncPaginate
                                                        menuPlacement="auto"
                                                        styles={{ ...selectCustomStyles }}
                                                        value={
                                                            appointment.facility_id
                                                                ? {
                                                                    value: appointment.facility_id,
                                                                    label:
                                                                        appointment.facilityName ||
                                                                        facilities.find((facility) => facility.id === appointment.facility_id)?.facility_name ||
                                                                        '',
                                                                }
                                                                : null
                                                        }
                                                        loadOptions={filteredFacilitiesLoadOptions}
                                                        isClearable={false}
                                                        escapeClearsValue
                                                        onChange={(obj) =>
                                                            updateAppointmentField(index, 'facility_id', obj?.value, { facilityName: obj?.label || '' })
                                                        }
                                                        additional={{ page: 1 }}
                                                    />
                                                </div>
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.vendorId')}</span>
                                                </div>
                                                <input className="input-in type_inp" value={appointment.interpreterCode || ''} readOnly />
                                            </label>

                                            <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.comments')}</span>
                                                </div>
                                                <div style={{ width: '100%' }}>
                                                    <Select
                                                        name={`comments_${index}`}
                                                        options={commentOptions}
                                                        value={commentOptions.find((option) => option.value === appointment.comments) || null}
                                                        isClearable={false}
                                                        disabledSearch={true}
                                                        onChange={(value) => updateAppointmentField(index, 'comments', value)}
                                                    />
                                                </div>
                                            </label>

                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.requestorName')}</span>
                                                </div>
                                                <input className="input-in type_inp" value={appointment.requesterName || ''} onChange={(event) => updateAppointmentField(index, 'requesterName', event.target.value)} />
                                            </label>
                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.requestorEmail')}</span>
                                                </div>
                                                <input className="input-in type_inp" value={appointment.requesterEmail || ''} onChange={(event) => updateAppointmentField(index, 'requesterEmail', event.target.value)} />
                                            </label>
                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                <div className="col-3">
                                                    <span>{t('invoice.requestorPhone')}</span>
                                                </div>
                                                <input className="input-in type_inp" value={appointment.requesterPhone || ''} onChange={(event) => updateAppointmentField(index, 'requesterPhone', event.target.value)} />
                                            </label>

                                            <div className="mt-3 pt-2 border-top">
                                                <h5 className="mb-2 font-bold text-gray-700">{t('invoice.costBreakdownPricing')}</h5>
                                                <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                    <div className="col-3">
                                                        <span>{t('invoice.appliedRate')}</span>
                                                    </div>
                                                    <input className="input-in type_inp" type="number" step="0.01" value={appointment.rate || ''} onChange={(event) => updateAppointmentField(index, 'rate', event.target.value)} />
                                                </label>
                                                <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                    <div className="col-3">
                                                        <span>{t('invoice.rateUnit')}</span>
                                                    </div>
                                                    <div style={{ width: '100%' }}>
                                                        <Select
                                                            name={`rate_unit_${index}`}
                                                            options={[{ label: 'Hour', value: 'hour' }, { label: 'Minute', value: 'minute' }]}
                                                            // value={[{ label: 'Hour', value: 'hour' }, { label: 'Minute', value: 'minute' }].find((option) => option.value === (appointment.rateUnit || 'hour')) || null}
                                                            value={{ label: 'Hour', value: 'hour' }}
                                                            isClearable={false}
                                                            disabledSearch={true}
                                                            onChange={(value) => updateAppointmentField(index, 'rateUnit', value)}
                                                            isDisabled={true}
                                                        />
                                                    </div>
                                                </label>
                                                <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                    <div className="col-3">
                                                        <span>{t('invoice.rushFee')} ($)</span>
                                                    </div>
                                                    <input className="input-in type_inp" type="number" step="0.01" min="0" value={appointment.rushFee ?? ''} onChange={(event) => updateAppointmentField(index, 'rushFee', event.target.value)} />
                                                </label>
                                                <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                    <div className="col-3">
                                                        <span>{t('invoice.mileageTotal')} ($)</span>
                                                    </div>
                                                    <input className="input-in type_inp" type="number" step="0.01" min="0" value={appointment.mileage ?? ''} onChange={(event) => updateAppointmentField(index, 'mileage', event.target.value)} />
                                                </label>
                                                <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                    <div className="col-3">
                                                        <span>{t('invoice.travelTotal')} ($)</span>
                                                    </div>
                                                    <input className="input-in type_inp" type="number" step="0.01" min="0" value={appointment.travel ?? ''} onChange={(event) => updateAppointmentField(index, 'travel', event.target.value)} />
                                                </label>
                                                <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                    <div className="col-3">
                                                        <span>{t('invoice.discount')} (%)</span>
                                                    </div>
                                                    <input className="input-in type_inp" type="number" step="0.01" min="0" value={appointment.discount ?? ''} onChange={(event) => updateAppointmentField(index, 'discount', event.target.value)} />
                                                </label>
                                                <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                    <div className="col-3">
                                                        <span>{t('invoice.totalAmount')} ($)</span>
                                                    </div>
                                                    <input className="input-in type_inp font-bold" type="number" step="0.01" value={Number(appointment.totalAmount || 0).toFixed(2)} readOnly />
                                                </label>
                                            </div>
                                        </td>
                                        <td className="font-bold text-end" style={{ verticalAlign: 'top', paddingTop: '15px' }}>
                                            ${Number(appointment.totalAmount || 0).toFixed(2)}
                                        </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        )}

                        {specialProjects.length > 0 && (
                            <div className="invoice-table-wrapper">
                                <h2 className="mb-3" style={{ fontWeight: 700 }}>{t('invoice.specialProject')}</h2>
                                <table className="invoice-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>{t('invoice.description')}</th>
                                            <th>{t('invoice.totalCost')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupedSpecialProjects.map((group, groupIndex) => (
                                            <React.Fragment key={`${group.key}-${groupIndex}`}>
                                                {specialProjectGroupByConfig.length > 0 && (
                                                    <tr>
                                                        <td colSpan="3"><strong>{group.label}</strong></td>
                                                    </tr>
                                                )}
                                                {group.projects.map((project, index) => {
                                            return (
                                            <tr key={project.translation_invoice_id || index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    {show(project.projectNumber) && (
                                                        <div className="mb-2 font-bold" style={{ fontSize: '15px' }}>
                                                            <span>{t('invoice.projectNumber')}: {project.projectNumber}</span>
                                                        </div>
                                                    )}
                                                    <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.date')}</span>
                                                        </div>
                                                        <div className="input-image" style={{ padding: '0px' }}>
                                                        <input
                                                            className="input-in type_inp"
                                                            type="date"
                                                            // value={
                                                            //     project.project?.created_at
                                                            //         ? project.project.created_at.slice(0, 10)
                                                            //         : (project.created_at ? project.created_at.slice(0, 10) : '')
                                                            // }
                                                            value={project.created_at ? project.created_at.slice(0, 10) : ''}
                                                            onChange={(event) => updateSpecialProjectField(index, 'created_at', event.target.value)}
                                                        />
                                                        <img
                                                        src="/images/celender.png"
                                                        alt="celender Icon"
                                                        className="custom-icon"
                                                        />
                                                        </div>
                                                    </label>
                                                    <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.currentPhase')}</span>
                                                        </div>
                                                        <div style={{ width: '100%' }}>
                                                            <Select
                                                                name={`current_phase_${index}`}
                                                                options={currentPhaseOptions}
                                                                value={
                                                                    currentPhaseOptions.find(
                                                                        (option) =>
                                                                            option.value ===
                                                                            (project.current_phase ?? project.project?.translation_details?.current_phase)
                                                                    ) || null
                                                                }
                                                                isClearable={false}
                                                                disabledSearch={true}
                                                                onChange={(value) => updateSpecialProjectField(index, 'current_phase', value)}
                                                            />
                                                        </div>
                                                    </label>
                                                    
                                                    <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.sourceLanguage')}</span>
                                                        </div>
                                                        <div style={{ width: '100%' }}>
                                                            <AsyncPaginate
                                                                menuPlacement="auto"
                                                                styles={{ ...selectCustomStyles }}
                                                                value={(() => {
                                                                    const id = project.source_language_id ?? project.project?.source_language_id;
                                                                    const label = project.sourceLanguageName ?? project.project?.language?.name ?? languages.find((l) => l.id === id)?.name ?? '';
                                                                    return id ? { value: id, label } : null;
                                                                })()}
                                                                loadOptions={filteredLanguagesLoadOptions}
                                                                isClearable={false}
                                                                escapeClearsValue
                                                                onChange={(obj) => updateSpecialProjectField(index, 'source_language_id', obj?.value, { sourceLanguageName: obj?.label || '' })}
                                                                additional={{ page: 1 }}
                                                                isDisabled={true}
                                                            />
                                                        </div>
                                                    </label>
                                                    <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.targetLanguage')}</span>
                                                        </div>
                                                        <div style={{ width: '100%' }}>
                                                            <AsyncPaginate
                                                                menuPlacement="auto"
                                                                styles={{ ...selectCustomStyles }}
                                                                value={(() => {
                                                                    const id = project.target_language_id ?? project.project?.translation_target_languages?.[0]?.language_id;
                                                                    const label = project.targetLanguageName ?? project.project?.translation_target_languages?.[0]?.language?.name ?? languages.find((l) => l.id === id)?.name ?? '';
                                                                    return id ? { value: id, label } : null;
                                                                })()}
                                                                loadOptions={filteredLanguagesLoadOptions}
                                                                isClearable={false}
                                                                escapeClearsValue
                                                                onChange={(obj) => updateSpecialProjectField(index, 'target_language_id', obj?.value, { targetLanguageName: obj?.label || '' })}
                                                                additional={{ page: 1 }}
                                                            />
                                                        </div>
                                                    </label>

                                                    <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.subAccount')}</span>
                                                        </div>
                                                        <div style={{ width: '100%' }}>
                                                            <Select
                                                                name={`sub_account_${index}`}
                                                                options={subAccountOptions}
                                                                value={subAccountOptions.find((option) => option.value === project.sub_account_id) || null}
                                                                isClearable={false}
                                                                disabledSearch={true}
                                                                onChange={(value) => updateSpecialProjectField(index, 'sub_account_id', value)}
                                                            />
                                                        </div>
                                                    </label>

                                                    <label className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.department')}</span>
                                                        </div>
                                                        <div style={{ width: '100%' }}>
                                                            <Select
                                                                name={`department_${index}`}
                                                                options={departmentOptions}
                                                                value={departmentOptions.find((option) => option.value === project.department_id) || null}
                                                                isClearable={false}
                                                                disabledSearch={true}
                                                                onChange={(value) => updateSpecialProjectField(index, 'department_id', value)}
                                                            />
                                                        </div>
                                                    </label>

                                                    {Number(project.project?.translation_details?.interpreter_name_show_invoice) === 1 && (
                                                        <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                            <div className="col-3">
                                                                <span>{t('invoice.vendorName')}</span>
                                                            </div>
                                                            <input className="input-in type_inp" value={project.vendorName || ''} readOnly />
                                                        </label>
                                                    )}
                                                    
                                                    {Number(project.project?.translation_details?.requester_details_show_invoice) === 1 && (
                                                        <>
                                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                                <div className="col-3">
                                                                    <span>{t('invoice.requestorName')}</span>
                                                                </div>
                                                                <input
                                                                    className="input-in type_inp"
                                                                    value={project.requesterName || ''}
                                                                    onChange={(event) => updateSpecialProjectField(index, 'requesterName', event.target.value)}
                                                                />
                                                            </label>
                                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                                <div className="col-3">
                                                                    <span>{t('invoice.requestorEmail')}</span>
                                                                </div>
                                                                <input
                                                                    className="input-in type_inp"
                                                                    value={project.requesterEmail || ''}
                                                                    onChange={(event) => updateSpecialProjectField(index, 'requesterEmail', event.target.value)}
                                                                />
                                                            </label>
                                                            <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                                <div className="col-3">
                                                                    <span>{t('invoice.requestorPhone')}</span>
                                                                </div>
                                                                <input
                                                                    className="input-in type_inp"
                                                                    value={project.requesterPhone || ''}
                                                                    onChange={(event) => updateSpecialProjectField(index, 'requesterPhone', event.target.value)}
                                                                />
                                                            </label>
                                                        </>
                                                    )}

                                                    <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>
                                                                {(() => {
                                                                    const phase = (project.current_phase || '').toLowerCase();
                                                                    if (phase === 'review') return t('invoice.totalPageCount');
                                                                    if (phase === 'transcription' || phase === 'other') return t('invoice.totalAudioMinutes');
                                                                    return t('invoice.totalWordCount');
                                                                })()}
                                                            </span>
                                                        </div>
                                                        <input
                                                            className="input-in type_inp"
                                                            type="number"
                                                            min="0"
                                                            value={project.total_words_count ?? ''}
                                                            onChange={(event) => updateSpecialProjectField(index, 'total_words_count', event.target.value)}
                                                        />
                                                    </label>

                                                    <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.rate')} ($)</span>
                                                        </div>
                                                        <input
                                                            className="input-in type_inp"
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={project.rate ?? ''}
                                                            onChange={(event) => updateSpecialProjectField(index, 'rate', event.target.value)}
                                                        />
                                                    </label>
                                                    <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.formatting')} ($)</span>
                                                        </div>
                                                        <input
                                                            className="input-in type_inp"
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={project.formatting ?? ''}
                                                            onChange={(event) => updateSpecialProjectField(index, 'formatting', event.target.value)}
                                                        />
                                                    </label>
                                                    <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.rushFee')} ($)</span>
                                                        </div>
                                                        <input
                                                            className="input-in type_inp"
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={project.rush_fee ?? ''}
                                                            onChange={(event) => updateSpecialProjectField(index, 'rush_fee', event.target.value)}
                                                        />
                                                    </label>
                                                    <label className="row-primary d-flex align-items-center justify-content-start gap-2 mb-2">
                                                        <div className="col-3">
                                                            <span>{t('invoice.totalCost')} ($)</span>
                                                        </div>
                                                        <input
                                                            className="input-in type_inp font-bold"
                                                            type="number"
                                                            step="0.01"
                                                            value={Number(project.amount || 0).toFixed(2)}
                                                            readOnly
                                                        />
                                                    </label>
                                                </td>
                                                <td>
                                                    ${Number(project.amount || 0).toFixed(2)}
                                                </td>
                                            </tr>
                                            );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <table className="info-container last_info">
                        <tbody>
                            <tr>
                                <th className="text-end last_due">{t('invoice.totalDue')}</th>
                                <th className="text-end last_due">${totalBill}</th>
                            </tr>
                        </tbody>
                    </table>
                </section>
            </div>

            {loader && <FullPageLoader />}
        </>
    );
};

export default EditPdfForm;